package com.mgacreative.hotelru.feature.reservation.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotelru.domain.model.Reservation
import com.mgacreative.hotelru.domain.model.Room
import com.mgacreative.hotelru.domain.repository.ReservationRepository
import com.mgacreative.hotelru.domain.repository.RoomRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.datetime.*

data class NewCalendarState(
    val rooms: List<Room> = emptyList(),
    val reservations: List<Reservation> = emptyList(),
    val filteredReservations: List<Reservation> = emptyList(),
    val searchQuery: String = "",
    val startDate: LocalDate = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date,
    val dates: List<LocalDate> = emptyList(),
    val isLoading: Boolean = false,
    val error: String? = null
)

class NewReservationCalendarViewModel(
    private val roomRepository: RoomRepository,
    private val reservationRepository: ReservationRepository
) : ViewModel() {

    private val _state = MutableStateFlow(NewCalendarState())
    val state: StateFlow<NewCalendarState> = _state.asStateFlow()

    init {
        loadData()
    }

    fun loadData() {
        viewModelScope.launch {
            _state.value = _state.value.copy(isLoading = true, error = null)
            
            try {
                val currentStart = _state.value.startDate
                val dates = (0 until 14).map { currentStart.plus(it, DateTimeUnit.DAY) }
                
                val rooms = roomRepository.getRooms()
                val reservations = reservationRepository.getReservations()
                
                _state.value = _state.value.copy(
                    rooms = rooms,
                    reservations = reservations,
                    filteredReservations = reservations,
                    dates = dates,
                    isLoading = false
                )
            } catch (e: Exception) {
                _state.value = _state.value.copy(
                    isLoading = false,
                    error = e.message
                )
            }
        }
    }

    fun shiftDays(days: Int) {
        val newStart = _state.value.startDate.plus(days, DateTimeUnit.DAY)
        val dates = (0 until 14).map { newStart.plus(it, DateTimeUnit.DAY) }
        _state.value = _state.value.copy(startDate = newStart, dates = dates)
    }

    fun resetToToday() {
        val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
        val dates = (0 until 14).map { today.plus(it, DateTimeUnit.DAY) }
        _state.value = _state.value.copy(startDate = today, dates = dates)
    }

    fun updateSearchQuery(query: String) {
        val lowerQuery = query.lowercase()
        val filtered = if (query.isBlank()) {
            _state.value.reservations
        } else {
            _state.value.reservations.filter {
                it.guests?.firstName?.lowercase()?.contains(lowerQuery) == true ||
                it.guests?.lastName?.lowercase()?.contains(lowerQuery) == true ||
                it.roomType?.lowercase()?.contains(lowerQuery) == true ||
                it.checkInDate.contains(lowerQuery) ||
                it.checkOutDate.contains(lowerQuery)
            }
        }
        _state.value = _state.value.copy(
            searchQuery = query,
            filteredReservations = filtered
        )
    }

    fun deleteReservation(reservationId: String) {
        viewModelScope.launch {
            try {
                reservationRepository.deleteReservation(reservationId)
                loadData() // reload
            } catch (e: Exception) {
                _state.value = _state.value.copy(error = "Silme işlemi başarısız: ${e.message}")
            }
        }
    }

    fun updateReservation(reservation: com.mgacreative.hotelru.domain.model.Reservation) {
        viewModelScope.launch {
            try {
                reservationRepository.updateReservation(reservation)
                loadData() // reload
            } catch (e: Exception) {
                _state.value = _state.value.copy(error = "Güncelleme işlemi başarısız: ${e.message}")
            }
        }
    }
}
